import { useMemo } from 'react';
import { useStore } from '../state/store';
import { StatCard } from '../components/StatCard';
import { GlossaryTerm } from '../components/GlossaryTerm';
import { TopBar } from '../components/TopBar';
import { NetWorthChart, type ChartEvent } from '../components/NetWorthChart';
import { YearRhythmStrip } from '../components/YearRhythmStrip';
import { LoansStrip } from '../components/LoansStrip';
import { TaxCard } from '../components/TaxCard';
import { AccountsPanel } from '../components/AccountsPanel';
import { Timeline, type TimelineEvent } from '../components/Timeline';
import { ScenarioPanel } from '../components/ScenarioPanel';
import { useState } from 'react';
import { Button, Icon, ProblemPanel } from '../ui';
import { fmtMoney, fmtCompact, fmtPct, fmtDate, relativeDate } from '../utils/format';
import { getProjectionProblems, historicalSeries } from '../engine/projection';
import { EVENT_META, eventHeadline } from '../events/meta';

interface Props {
  onGoTo: (tab: string) => void;
}

export function DashboardPage({ onGoTo }: Props) {
  const {
    dashboard,
    projection,
    baselineProjection,
    compareProjections,
    activeScenario,
    assumptions,
    snapshots,
    accounts,
    currentBalances,
  } = useStore();
  const latestSnapshot = snapshots[0];
  const hist = historicalSeries(snapshots, accounts);
  const [focusEventId, setFocusEventId] = useState<string | null>(null);

  // Slice 6: top-3 problems summary. Section is hidden entirely when the
  // engine reports nothing — no empty state needed (the absence of issues
  // is itself a positive signal).
  const topProblems = useMemo(
    () => getProjectionProblems(projection.years, { limit: 3 }),
    [projection.years],
  );

  // Every enabled event in the active scenario — rendered as annotated
  // markers on the projection chart. Color follows each event's tone so the
  // user can see at a glance whether a year is an outflow (red), an inflow
  // (green), a housing change (blue), etc.
  const chartEvents = useMemo<ChartEvent[]>(() => {
    return activeScenario.events
      .filter(e => e.enabled !== false)
      .map(e => {
        const meta = EVENT_META[e.kind];
        const name = ('name' in e && e.name) || meta.label;
        return {
          yearOffset: e.yearOffset,
          calendarYear: assumptions.startingCalendarYear + e.yearOffset,
          name,
          kind: e.kind,
          tone: meta.tone,
        };
      });
  }, [activeScenario, assumptions]);

  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    const events: TimelineEvent[] = [];
    const nowMs = Date.now();

    // Past: recent snapshots
    const histAsc = [...hist];
    for (let i = 0; i < histAsc.length; i++) {
      const h = histAsc[i];
      const prev = i > 0 ? histAsc[i - 1] : null;
      const delta = prev ? h.netWorth - prev.netWorth : 0;
      events.push({
        id: `snap-${h.date}`,
        sortMs: new Date(h.date).getTime(),
        type: 'snapshot',
        whenLabel: fmtDate(h.date),
        title: `Net worth ${fmtCompact(h.netWorth)}`,
        subtitle: prev
          ? `${delta >= 0 ? '+' : '−'}${fmtMoney(Math.abs(delta))} vs. prev`
          : 'First snapshot',
        amount: prev && delta !== 0 ? Math.abs(delta) : undefined,
        amountTone: delta > 0 ? 'pos' : delta < 0 ? 'neg' : 'neutral',
        onClick: () => onGoTo('history'),
        past: true,
      });
    }

    // "Now" marker
    events.push({
      id: 'now',
      sortMs: nowMs,
      type: 'now',
      whenLabel: 'Today',
      title: `Age ${assumptions.currentAge}`,
      subtitle: `NW ${fmtCompact(dashboard.currentNetWorth)}`,
      past: false,
    });

    // Future: active scenario's events
    for (const e of activeScenario.events) {
      const calendarYear = assumptions.startingCalendarYear + e.yearOffset;
      const age = assumptions.currentAge + e.yearOffset;
      const meta = EVENT_META[e.kind];
      const enabled = e.enabled;
      const label = ('name' in e && e.name) || meta.label;
      events.push({
        id: `ev-${e.id}`,
        sortMs: new Date(`${calendarYear}-06-15`).getTime(),
        type:
          e.kind === 'purchase' || e.kind === 'gift'
            ? 'purchase'
            : e.kind === 'retire'
              ? 'retirement'
              : 'snapshot',
        whenLabel: `${calendarYear} · age ${age}`,
        title: `${enabled ? '' : '(off) '}${label}`,
        subtitle: `${meta.label} · ${eventHeadline(e)}`,
        past: !enabled,
        onClick: () => onGoTo('scenarios'),
      });
    }

    // Future: mortgage payoff
    if (dashboard.mortgagePayoffYear) {
      events.push({
        id: 'payoff',
        sortMs: new Date(`${dashboard.mortgagePayoffYear}-12-15`).getTime(),
        type: 'payoff',
        whenLabel: `${dashboard.mortgagePayoffYear}`,
        title: 'Mortgage paid off',
        subtitle: 'P&I ends; escrow continues',
        past: false,
      });
    }

    // Future: retirement milestone
    const retireYear =
      assumptions.startingCalendarYear +
      (assumptions.targetRetirementAge - assumptions.currentAge);
    events.push({
      id: 'retire',
      sortMs: new Date(`${retireYear}-06-15`).getTime(),
      type: 'retirement',
      whenLabel: `${retireYear} · age ${assumptions.targetRetirementAge}`,
      title: 'Target retirement',
      subtitle: `${fmtCompact(dashboard.projectedNestEgg)} projected nest egg`,
      past: false,
    });

    return events;
  }, [hist, activeScenario, assumptions, dashboard, onGoTo]);

  return (
    <>
      <TopBar
        title={assumptions.displayName?.trim() || 'Cairn'}
        subtitle={latestSnapshot ? `Updated ${relativeDate(latestSnapshot.date)}` : 'No snapshots yet'}
        onManageScenarios={() => onGoTo('scenarios')}
        right={
          <Button
            size="sm"
            leading={<Icon name="plus" size={14} />}
            onClick={() => onGoTo('update')}
          >
            Update
          </Button>
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-4 pb-nav lg:px-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-6 xl:grid-cols-[minmax(0,1fr)_500px]">
          {/* ─── Main column ─── */}
          <div className="space-y-4">
            {/* Hero row */}
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
              <div className="card card-dark relative overflow-hidden p-5">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-50 to-transparent dark:from-ink-700 dark:to-transparent pointer-events-none" />
                <div className="relative">
                  <div className="label">Net Worth</div>
                  <div className="mt-1 font-mono text-4xl font-bold tracking-tight text-ink-900 dark:text-ink-50 md:text-5xl">
                    {fmtMoney(dashboard.currentNetWorth)}
                  </div>
                  <div className="mt-1 text-sm text-ink-500 dark:text-ink-300">
                    <GlossaryTerm term="bucket">Investable</GlossaryTerm>{' '}
                    {fmtMoney(dashboard.currentInvestable)} · Age {dashboard.currentAge}
                  </div>
                  {hist.length >= 2 && (
                    <Sparkline points={hist.map(h => ({ date: h.date, value: h.netWorth }))} />
                  )}
                </div>
              </div>

              <div
                className={`card card-dark flex flex-col justify-center gap-3 p-5 border-l-4 ${
                  dashboard.onTrack ? 'border-l-pos-500' : 'border-l-neg-500'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-bold ${
                      dashboard.onTrack
                        ? 'bg-pos-50 text-pos-700 dark:bg-pos-700/20 dark:text-pos-500'
                        : 'bg-neg-50 text-neg-700 dark:bg-neg-700/20 dark:text-neg-500'
                    }`}
                  >
                    {dashboard.onTrack ? '✓' : '!'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">
                      {dashboard.onTrack ? 'On track to retire at ' : 'Gap at age '}
                      {dashboard.targetRetirementAge}
                    </div>
                    <div className="text-xs text-ink-500 dark:text-ink-300">
                      {dashboard.onTrack
                        ? `SWR income covers spend with ${fmtMoney(dashboard.annualSurplus)}/yr surplus.`
                        : `SWR income falls short by ${fmtMoney(-dashboard.annualSurplus)}/yr.`}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <MiniStat
                    label={<GlossaryTerm term="nest_egg">Nest egg</GlossaryTerm>}
                    value={fmtCompact(dashboard.projectedNestEgg)}
                    tone="accent"
                  />
                  <MiniStat label="Required" value={fmtCompact(dashboard.requiredNestEgg)} />
                  <MiniStat
                    label="Cushion"
                    value={fmtCompact(dashboard.cushion)}
                    tone={dashboard.cushion >= 0 ? 'pos' : 'neg'}
                  />
                </div>
              </div>
            </section>

            {topProblems.length > 0 && (
              <section className="card card-dark p-3" aria-label="Things to know about your plan">
                <div className="mb-2 flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold">Things to know about your plan</h2>
                  <button
                    type="button"
                    className="text-xs text-accent-600 dark:text-accent-500"
                    onClick={() => onGoTo('projection')}
                  >
                    See all →
                  </button>
                </div>
                <div className="space-y-2">
                  {topProblems.map(p => (
                    <ProblemPanel
                      key={p.id}
                      severity={p.severity}
                      title={p.title}
                      body={p.body}
                      actions={p.actions}
                      termRefs={p.termRefs}
                    />
                  ))}
                </div>
              </section>
            )}

            <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              <StatCard
                label={<GlossaryTerm term="swr">SWR income</GlossaryTerm>}
                value={fmtCompact(dashboard.safeAnnualWithdrawal)}
                hint={
                  <>
                    @ {fmtPct(assumptions.swr, 2)}{' '}
                    <GlossaryTerm term="swr">SWR</GlossaryTerm>
                  </>
                }
                compact
              />
              <StatCard
                label="Required spend"
                value={fmtCompact(dashboard.requiredSpend)}
                hint="per year retired"
                compact
              />
              <StatCard
                label="Est. income"
                value={fmtCompact(dashboard.annualSavingsRate + dashboard.currentAnnualSpend)}
                hint="savings + spend"
                compact
              />
              <StatCard
                label="Annual savings"
                value={fmtCompact(dashboard.annualSavingsRate)}
                hint={
                  <>
                    <GlossaryTerm term="401k">401k</GlossaryTerm> +{' '}
                    <GlossaryTerm term="taxable_brokerage">Tax</GlossaryTerm> +{' '}
                    <GlossaryTerm term="hysa">HYSA</GlossaryTerm>
                  </>
                }
                compact
              />
              <StatCard
                label="Annual spend"
                value={fmtCompact(dashboard.currentAnnualSpend)}
                hint="today"
                compact
              />
            </section>

            <div id="dashboard-scenario-panel" className="lg:hidden">
              <ScenarioPanel onGoTo={onGoTo} focusEventId={focusEventId} onFocusEventHandled={() => setFocusEventId(null)} />
            </div>

            <section className="card card-dark p-2 sm:p-3">
              <div className="flex items-center justify-between px-2 pb-1">
                <h2 className="text-sm font-semibold">Projection (real $)</h2>
                <button
                  className="text-xs text-accent-600 dark:text-accent-500"
                  onClick={() => onGoTo('projection')}
                >
                  Details →
                </button>
              </div>
              <NetWorthChart
                years={projection.years}
                baselineYears={baselineProjection.years}
                compareSeries={compareProjections.map(c => ({
                  id: c.scenario.id,
                  label: c.scenario.name,
                  color: c.scenario.color,
                  years: c.projection.years,
                }))}
                targetRetirementAge={assumptions.targetRetirementAge}
                requiredNestEgg={projection.requiredNestEgg}
                actuals={hist}
                startingCalendarYear={assumptions.startingCalendarYear}
                currentAge={assumptions.currentAge}
                chartEvents={chartEvents}
                compensation={assumptions.compensation}
                contributionRules={assumptions.contributionRules}
              />
            </section>

            <YearRhythmStrip
              years={projection.years}
              assumptions={assumptions}
              onGoTo={onGoTo}
            />

            <LoansStrip
              years={projection.years}
              mortgage={projection.mortgage}
              startingCalendarYear={assumptions.startingCalendarYear}
              totalYears={projection.years.length}
              onOpenEvent={id => {
                setFocusEventId(id);
                // Scroll the scenario panel into view on mobile; on desktop
                // it's in the gutter so it's already visible.
                requestAnimationFrame(() => {
                  document
                    .getElementById('dashboard-scenario-panel')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <TaxCard year={projection.years[0]} label="this year" />
              <TaxCard
                year={projection.years[dashboard.targetRetirementAge - dashboard.currentAge]}
                label={`at retirement (age ${dashboard.targetRetirementAge})`}
              />
            </div>

            <AccountsPanel
              accounts={accounts}
              snapshots={snapshots}
              currentBalances={currentBalances}
              onEdit={() => onGoTo('accounts')}
            />

            <Timeline
              events={timelineEvents}
              title="Activity"
              right={
                <button
                  className="text-[11px] font-semibold text-accent-600 dark:text-accent-500"
                  onClick={() => onGoTo('history')}
                >
                  History →
                </button>
              }
              maxEvents={16}
            />

            <section className="card card-dark p-4">
              <div className="label mb-3">Rules of thumb</div>
              <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
                <Rule
                  left="$1 spent today costs"
                  right={fmtMoney(dashboard.costOf1TodayAtRetirement, { decimals: 2 })}
                  sub={`at age ${dashboard.targetRetirementAge}`}
                />
                <Rule
                  left="…and forever reduces income by"
                  right={`${fmtMoney(dashboard.costOf1TodayIncome, { decimals: 2 })}/yr`}
                />
                <Rule
                  left="Mortgage payoff"
                  right={dashboard.mortgagePayoffYear?.toString() ?? '—'}
                />
              </div>
            </section>
          </div>

          {/* ─── Right gutter: sticky scenario panel on lg+ ─── */}
          <aside className="hidden lg:block">
            <div
              className="sticky overflow-y-auto overscroll-contain"
              style={{
                top: 'calc(var(--top-chrome, 0px) + 1rem)',
                maxHeight: 'calc(100dvh - var(--top-chrome, 0px) - 2rem)',
              }}
            >
              <ScenarioPanel onGoTo={onGoTo} focusEventId={focusEventId} onFocusEventHandled={() => setFocusEventId(null)} />
            </div>
          </aside>
        </div>
      </main>
    </>
  );
}

function MiniStat({
  label,
  value,
  tone = 'neutral',
}: {
  label: React.ReactNode;
  value: string;
  tone?: 'neutral' | 'pos' | 'neg' | 'accent';
}) {
  const c =
    tone === 'pos'
      ? 'text-pos-700 dark:text-pos-500'
      : tone === 'neg'
        ? 'text-neg-700 dark:text-neg-500'
        : tone === 'accent'
          ? 'text-accent-700 dark:text-accent-500'
          : 'text-ink-900 dark:text-ink-50';
  return (
    <div className="rounded-lg bg-ink-50 px-2 py-1.5 dark:bg-ink-800/60">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
        {label}
      </div>
      <div className={`font-mono text-sm font-bold tabular-nums ${c}`}>{value}</div>
    </div>
  );
}

function Rule({ left, right, sub }: { left: string; right: string; sub?: string }) {
  return (
    <div className="flex items-baseline justify-between border-b border-ink-100 py-2 last:border-b-0 last:pb-0 dark:border-ink-700 md:border-b-0 md:py-1">
      <div>
        <div className="text-sm text-ink-700 dark:text-ink-100">{left}</div>
        {sub && <div className="text-xs text-ink-500 dark:text-ink-300">{sub}</div>}
      </div>
      <div className="font-mono text-sm font-semibold tabular-nums">{right}</div>
    </div>
  );
}

/**
 * Time-honest sparkline: x is proportional to real time between snapshots,
 * not sample index. Each snapshot is marked with a dot so uneven sampling
 * (a 6-month gap next to a weekly cadence) is visually obvious, not
 * smoothed away into a straight line.
 */
function Sparkline({ points }: { points: { date: string; value: number }[] }) {
  if (points.length < 2) return null;
  const w = 280;
  const h = 48;
  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const times = points.map(p => new Date(p.date).getTime());
  const tMin = times[0];
  const tMax = times[times.length - 1];
  const tSpan = tMax - tMin || 1;
  const xy = points.map((p, i) => ({
    x: ((times[i] - tMin) / tSpan) * w,
    y: h - ((p.value - min) / range) * h,
  }));
  const path = xy
    .map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`)
    .join(' ');
  const trend = points[points.length - 1].value >= points[0].value;
  const color = trend ? '#059669' : '#ef4444';
  return (
    <div className="mt-3">
      <svg
        className="h-12 w-full"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        <path d={path} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {xy.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={2} fill={color} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-ink-500 dark:text-ink-400">
        <span>{relativeDate(points[0].date)}</span>
        <span>{relativeDate(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
}
